// @flow
import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog } from 'components/Dialog'
import styles from './styles'

const updateEmailAt = (emails, index, value) => {
  return emails.map((item, itemIndex) => {
    if (itemIndex !== index) return item
    return { ...item, email: value }
  })
}

const removeEmailAt = (emails, index) => {
  return emails.filter((_, itemIndex) => itemIndex !== index)
}

const isValidEmail = (email = '') => {
  const trimmedEmail = email.trim()
  if (!trimmedEmail) return true
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)
}

const extractEmailErrorMessage = (errorPayload) => {
  if (!errorPayload) return null
  if (typeof errorPayload === 'string') return errorPayload

  const nestedErrors = errorPayload
    && errorPayload.error
    && Array.isArray(errorPayload.error.errors)
    ? errorPayload.error.errors
    : []

  const emailFieldError = nestedErrors.find(({ location }) => location === 'email')
  if (emailFieldError && emailFieldError.message === 'has invalid format') {
    return 'Correct email required'
  }

  if (emailFieldError && emailFieldError.message) {
    return emailFieldError.message
  }

  if (typeof errorPayload.message === 'string') return errorPayload.message
  return null
}

const ContactEmailsField = ({ input, meta, error }) => {
  const [copiedIndex, setCopiedIndex] = React.useState(null)
  const [pendingDeleteIndex, setPendingDeleteIndex] = React.useState(null)
  const [touchedIndexes, setTouchedIndexes] = React.useState({})
  const timeoutRef = React.useRef(null)

  const list = Array.isArray(input.value) && input.value.length
    ? input.value
    : [{ email: '' }]

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const onAdd = () => {
    input.onChange([...list, { email: '' }])
  }

  const onDelete = (index) => {
    input.onChange(removeEmailAt(list, index))
    setTouchedIndexes((prev) => {
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  const onConfirmDelete = () => {
    if (pendingDeleteIndex === null) return
    onDelete(pendingDeleteIndex)
    setPendingDeleteIndex(null)
  }

  const onChangeEmail = (index, event) => {
    input.onChange(updateEmailAt(list, index, event.target.value))
  }

  const onBlurEmail = (index) => {
    setTouchedIndexes((prev) => ({ ...prev, [index]: true }))
    input.onBlur()
  }

  const onCopy = (value, index) => {
    if (
      !value
      || typeof navigator === 'undefined'
      || !navigator.clipboard
      || !navigator.clipboard.writeText
    ) return

    navigator.clipboard.writeText(value).then(() => {
      setCopiedIndex(index)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => setCopiedIndex(null), 1200)
    })
  }

  const isFieldInvalid = (email, index) =>
    !isValidEmail(email) && (touchedIndexes[index] || (meta && meta.submitFailed))

  const hasInvalidEmail = list.some(({ email = '' }, index) => isFieldInvalid(email, index))

  const backendEmailErrorMessage = extractEmailErrorMessage(error) || extractEmailErrorMessage(meta && meta.error)
  const hasEmailError = hasInvalidEmail || Boolean(backendEmailErrorMessage)
  const emailErrorMessage = hasInvalidEmail
    ? 'Correct email required'
    : backendEmailErrorMessage

  return (
    <section className={styles.cardMarginBot}>
      <div className={styles.sectionTitle}>
        {hasEmailError
          ? (
            <div className={styles.errorSection}>
              <h5 className={styles.phoneSectionError}>
                Email — {emailErrorMessage}
              </h5>
            </div>
          )
          : <h5>Email</h5>}
      </div>
      <div className={styles.sectionNoMargin}>
        <div className={styles.emailHeaderRow}>
          <div className={styles.emailHeader}>Email Address</div>
        </div>
        {list.map(({ email = '' }, index) => (
          <div className={styles.emailRow} key={`email-${index}`}>
            <div className={styles.emailInputWrapper}>
              <input
                className={`${styles.input} ${isFieldInvalid(email, index) || backendEmailErrorMessage ? styles.emailInputError : ''}`}
                onBlur={() => onBlurEmail(index)}
                onChange={(event) => onChangeEmail(index, event)}
                placeholder='test@example.com'
                type='text'
                value={email}
              />
              {isFieldInvalid(email, index) && (
                <span className={styles.emailInlineError}>
                  Correct email required
                </span>
              )}
            </div>
            <div className={styles.emailActions}>
              <button
                className={styles.emailActionButton}
                onClick={() => onCopy(email, index)}
                title='Copy email'
                type='button'
              >
                <FontAwesomeIcon
                  icon={copiedIndex === index ? ['fas', 'check'] : ['far', 'copy']}
                />
              </button>
              <a
                className={styles.emailActionButton}
                href={email ? `mailto:${email}` : undefined}
                title='Send email'
              >
                <FontAwesomeIcon icon={['far', 'paper-plane']} />
              </a>
              <button
                className={styles.emailActionButton}
                onClick={() => setPendingDeleteIndex(index)}
                title='Delete email'
                type='button'
              >
                <FontAwesomeIcon icon={['far', 'times-circle']} />
              </button>
            </div>
          </div>
        ))}
        <button className={styles.addEmailButton} onClick={onAdd} type='button'>
          Add New
        </button>
      </div>
      {pendingDeleteIndex !== null && (
        <Dialog
          cancelButtonText='Cancel'
          confirmButtonText='Yes, Delete'
          headerText={`Delete ${(list[pendingDeleteIndex] && list[pendingDeleteIndex].email) || 'this email'}?`}
          onCancel={() => setPendingDeleteIndex(null)}
          onConfirm={onConfirmDelete}
        />
      )}
    </section>
  )
}

export default ContactEmailsField